import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { ImageComponent } from "../wasteQuality/ImageComponent";
import {
  elapsedTimeContainer,
  formattedDate,
  formattedTime,
} from "../../formatted/FormattedDateAndTime";
import { useTranslation } from "react-i18next";
import { formattedStatus } from "../../formatted/FormattedText";
import { capitalizeFirstLetter } from "../../utils/Date";

export const ContainerRow = ({ data }) => {
  const { t, i18n } = useTranslation();

  const setStatusColor = (statusContainer) => {
    if (statusContainer === "drop_driver") {
      return "#7ED958";
    } else if (statusContainer === "remove_driver") {
      return "#FF1617";
    } else if (statusContainer === "collect") {
      return "#FFBD59";
    } else if (statusContainer === "maintenance") {
      return "#A6A6A6";
    } else if (statusContainer === "store_logistic") {
      return "#38B6FF";
    } else if (statusContainer === "in_use_logistic") {
      return "#FF66C4";
    } else {
      return "#A6A6A6";
    }
  };

  const styles = StyleSheet.create({
    container: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-evenly",
      paddingVertical: 10,
      width: "98.5%",
      borderBottom: "1px solid #eee",
      backgroundColor: "#FFFFFF",
      marginVertical: 2,
      marginHorizontal: 4,
      borderRadius: 5,
    },
    imageContainer: {
      marginHorizontal: 2,
      width: "15%",
    },
    infoBlock: {
      width: "15%",
      flexDirection: "column",
      alignItems: "flex-start",
      marginHorizontal: 8,
      textAlign: "left",
    },
    primaryText: {
      fontWeight: 500,
      fontSize: 8,
    },
    smallText: {
      fontSize: 7,
      color: "#888",
    },
    progressBarContainer: {
      marginTop: 4,
      width: "100%",
      height: 6,
      backgroundColor: "#eee",
      borderRadius: 3,
    },
    progressBarFill: {
      height: "100%",
      backgroundColor: "red",
      borderRadius: 3,
    },
    sortingErrors: {
      flexDirection: "row",
      alignItems: "center",
      fontSize: 8,
      marginTop: 5,
    },
    locationStatus: {
      flexDirection: "row",
      alignItems: "center",
    },
    circleDot: {
      width: 8,
      height: 8,
      backgroundColor: setStatusColor(data.status),
      borderRadius: 4,
      marginRight: 5,
    },
  });

  const weightToTonnes = (weightKg, weightUnit) =>
    weightUnit === "t" ? weightKg / 1000 : weightKg;

  const hasWeight = data?.weightKG && data.weightKG > 0;
  const weightValue = hasWeight
    ? weightToTonnes(data.weightKG, data.weightUnit)
    : "-";

  const fullWeight = hasWeight && data.fullWeight > 0 ? data.fullWeight : "-";

  const unitLabel = data.weightUnit === "kg" ? t("Kilogram") : t("ton");

  const isPlural = weightToTonnes(data.weightKG, data.weightUnit) > 1;

  return (
    <View style={styles.container}>
      <View style={styles.imageContainer}>
        <ImageComponent data={data} drawMaterial={false} height={70} />
      </View>

      <View style={styles.infoBlock}>
        <Text style={styles.primaryText}>
          {elapsedTimeContainer(data._time, i18n.language, true)}
        </Text>
        <Text style={styles.smallText}>
          {formattedDate(data._time, t)}, {formattedTime(data._time)}
        </Text>
      </View>

      <View
        style={{
          ...styles.infoBlock,
          width: "15%",
        }}
      >
        {data?.positioning && (
          <Text style={styles.primaryText}>{data.positioning}</Text>
        )}
        <Text style={styles.smallText}>{data.addressName}</Text>
      </View>

      <View style={styles.infoBlock}>
        <Text style={styles.primaryText}>{data.wasteName || "-"}</Text>
        {data.activatedFillingLevelOption && (
          <Text style={styles.primaryText}>{data.filling_level}%</Text>
        )}
      </View>

      <View style={styles.infoBlock}>
        <Text style={styles.primaryText}>
          {`${weightValue ? Number(weightValue).toFixed(1) : "-"} / ${fullWeight ? fullWeight : "-"}`}
        </Text>
        <Text style={styles.smallText}>
          {unitLabel}
          {isPlural ? "s" : ""}
        </Text>
      </View>

      {data.packmatActive && (
        <View style={styles.infoBlock}>
          <Text style={styles.primaryText}>{data.numberOfPackmat ?? "-"}</Text>
          <Text style={styles.smallText}>{t("Packmat")}</Text>
        </View>
      )}

      {data.activatedSortingErrorOption && (
        <View style={styles.infoBlock}>
          <Text style={styles.primaryText}>{data.numberOfAnomaly ?? "-"}</Text>
          <Text style={styles.smallText}>
            {capitalizeFirstLetter(
              data.numberOfAnomaly > 1
                ? t("sorting_errors")
                : t("sorting_error"),
            )}
          </Text>
        </View>
      )}

      <View style={styles.infoBlock}>
        <View style={styles.locationStatus}>
          <View style={styles.circleDot} />
          <Text style={styles.primaryText}>
            {" "}
            {formattedStatus(data.status)}{" "}
          </Text>
        </View>
      </View>
    </View>
  );
};
